import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { SpaBadge } from '@/components/ui/spa-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Building2, Users, Briefcase, ArrowRight, CheckCircle, 
  Shield, TrendingUp, FileText, Search, Target, Award,
  MapPin, Calendar, Handshake, DollarSign
} from 'lucide-react';

interface SpaOpportunity {
  id: string;
  title: string;
  type: 'job' | 'tender' | 'jv' | 'training' | 'service_offer';
  sector: 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
  country: string;
  city: string | null;
  spa_pillar: string | null;
  company: { name: string };
}

interface Partner {
  id: string;
  name: string;
  sectors: string[];
  country: string;
  verified: boolean;
  company_type: string;
}

const ForCompanies = () => {
  const { t } = useTranslation();
  const [spaOpps, setSpaOpps] = useState<SpaOpportunity[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      supabase
        .from('opportunities')
        .select('id, title, type, sector, country, city, spa_pillar, company:companies(name)')
        .eq('spa_linked', true)
        .eq('status', 'open')
        .order('published_at', { ascending: false })
        .limit(6),
      supabase
        .from('companies')
        .select('id, name, sectors, country, verified, company_type')
        .eq('verified', true)
        .neq('company_type', 'us_company')
        .limit(8)
    ]).then(([oppsRes, partnersRes]) => {
      setSpaOpps((oppsRes.data as unknown as SpaOpportunity[]) || []);
      setPartners((partnersRes.data as unknown as Partner[]) || []);
      setLoading(false);
    });
  }, []);

  const features = [
    {
      icon: Target,
      title: t('forCompanies.features.spaProjects.title'),
      description: t('forCompanies.features.spaProjects.description'),
      color: 'bg-primary/10 text-primary'
    },
    {
      icon: Users,
      title: t('forCompanies.features.vettedPartners.title'),
      description: t('forCompanies.features.vettedPartners.description'),
      color: 'bg-secondary/10 text-secondary'
    },
    {
      icon: Award,
      title: t('forCompanies.features.talentAccess.title'),
      description: t('forCompanies.features.talentAccess.description'),
      color: 'bg-accent/10 text-accent-foreground'
    },
    {
      icon: Shield,
      title: t('forCompanies.features.dueDiligence.title'),
      description: t('forCompanies.features.dueDiligence.description'),
      color: 'bg-muted text-muted-foreground'
    },
    {
      icon: TrendingUp,
      title: t('forCompanies.features.investment.title'),
      description: t('forCompanies.features.investment.description'),
      color: 'bg-primary/10 text-primary'
    },
    {
      icon: FileText,
      title: t('forCompanies.features.riskAssessment.title'),
      description: t('forCompanies.features.riskAssessment.description'),
      color: 'bg-secondary/10 text-secondary'
    }
  ];

  const spaPillars = [
    { key: 'critical_minerals', icon: '⛏️', value: '$500B+' },
    { key: 'infrastructure', icon: '🏗️', value: '15 Projects' },
    { key: 'industrialization', icon: '🏭', value: '200+ SMEs' },
    { key: 'governance', icon: '🏛️', value: '5 Programs' }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary to-secondary">
        <div className="container relative py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-4xl text-center text-primary-foreground"
          >
            <h1 className="font-heading text-balance text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              {t('forCompanies.hero.title')}
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/80 md:text-xl">
              {t('forCompanies.hero.subtitle')}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
                <Link to="/opportunities?spa=true">
                  {t('forCompanies.cta.exploreSpa')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" asChild>
                <Link to="/suppliers">{t('forCompanies.cta.findPartners')}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* SPA Pillars Overview */}
      <section className="container py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl font-bold mb-4">{t('forCompanies.spaPillars.title')}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">{t('forCompanies.spaPillars.description')}</p>
        </motion.div>
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {spaPillars.map((pillar, index) => (
            <motion.div
              key={pillar.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
            >
              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">{pillar.icon}</div>
                  <h3 className="font-semibold mb-2">{t(`spaPillars.${pillar.key}`)}</h3>
                  <div className="text-2xl font-bold text-primary">{pillar.value}</div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t(`forCompanies.spaPillars.${pillar.key}Description`)}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Key Features */}
      <section className="bg-muted/30 py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4">{t('forCompanies.features.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('forCompanies.features.description')}</p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              >
                <Card className="h-full transition-shadow hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${feature.color}`}>
                      <feature.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SPA Opportunities */}
      <section className="container py-16">
        <div className="flex justify-between items-center mb-8">
          <h2 className="font-heading text-3xl font-bold">{t('forCompanies.spaOpportunities.title')}</h2>
          <Button variant="outline" asChild>
            <Link to="/opportunities?spa=true">
              {t('forCompanies.spaOpportunities.viewAll')} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-40 w-full" />)}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {spaOpps.map(opp => (
              <Link key={opp.id} to={`/opportunities/${opp.id}`}>
                <Card className="h-full transition-shadow hover:shadow-lg">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <TypeBadge type={opp.type} />
                      <SectorBadge sector={opp.sector} />
                      {opp.spa_pillar && <SpaBadge pillar={opp.spa_pillar as any} size="sm" />}
                    </div>
                    <h3 className="font-semibold line-clamp-2">{opp.title}</h3>
                    <p className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Building2 className="h-3 w-3" />{opp.company?.name}
                    </p>
                    <p className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3" />{opp.city ? `${opp.city}, ` : ''}{opp.country}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Find Local Suppliers CTA */}
      <section className="bg-muted/30 py-16">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <h2 className="font-heading text-3xl font-bold mb-4">{t('landing.findSuppliers.title')}</h2>
              <p className="text-muted-foreground mb-6">
                {t('landing.findSuppliers.description')}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {t('companyProfile.types.congolese_sme_desc')}
              </p>
              <Button asChild>
                <Link to="/suppliers">
                  {t('landing.cta.findSuppliers')} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="flex-1">
              {loading ? (
                <div className="grid gap-4 grid-cols-2">
                  {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full" />)}
                </div>
              ) : (
                <div className="grid gap-4 grid-cols-2">
                  {partners.slice(0,4).map(partner => (
                    <Link key={partner.id} to={`/companies/${partner.id}`}>
                      <Card className="h-full transition-shadow hover:shadow-lg">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-sm line-clamp-1">{partner.name}</h3>
                            {partner.verified && <CheckCircle className="h-4 w-4 text-green-600" />}
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">{partner.country}</p>
                          <div className="flex flex-wrap gap-1">
                            {(partner.sectors as string[])?.slice(0,2).map(sector => (
                              <Badge key={sector} variant="secondary" className="text-xs">{sector}</Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="container py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="rounded-2xl bg-gradient-to-r from-primary to-secondary p-8 text-center text-white md:p-12"
        >
          <h2 className="font-heading text-3xl font-bold md:text-4xl mb-4">
            {t('forCompanies.cta.title')}
          </h2>
          <p className="mx-auto max-w-2xl text-white/80 mb-8">
            {t('forCompanies.cta.description')}
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
              <Link to="/auth?mode=signup">{t('forCompanies.cta.getStarted')}</Link>
            </Button>
            <Button size="lg" variant="link" className="text-white" asChild>
              <Link to="/about-spa">{t('forCompanies.cta.learnMore')}</Link>
            </Button>
          </div>
        </motion.div>
      </section>
    </Layout>
  );
};

export default ForCompanies;